//deps
import { useCallback, useRef, useState } from "react";

//hooks
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

export const useElementDynamicHeight = (
    elementRef: React.RefObject<HTMLElement>,
): {
    elementHeight: number;
} => {
    const [dynamicHeight, setDynamicHeight] = useState(0);

    const resizeObserver = useRef<ResizeObserver | null>(null);
    const mutationObserver = useRef<MutationObserver | null>(null);

    const updateHeight = useCallback(() => {
        if (elementRef.current) {
            const contentHeight = elementRef.current.scrollHeight;
            if (contentHeight !== dynamicHeight) {
                // Using double requestAnimationFrame to ensure smoother height transition on Android devices
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        setDynamicHeight(contentHeight);
                    });
                });
            }
        }
    }, [dynamicHeight]);

    // Update height on content change
    useEffectOnce(() => {
        updateHeight();

        if (typeof window !== "undefined" && !resizeObserver.current && !mutationObserver.current) {
            if (typeof window.ResizeObserver !== "undefined") {
                resizeObserver.current = new ResizeObserver(updateHeight);

                if (elementRef.current) {
                    resizeObserver.current.observe(elementRef.current);
                }

                // Cleanup the observer on unmount
                return () => resizeObserver.current?.disconnect();
            } else if (typeof MutationObserver !== "undefined") {
                // Fallback for browsers that don't support ResizeObserver
                mutationObserver.current = new MutationObserver(updateHeight);

                if (elementRef.current) {
                    mutationObserver.current.observe(elementRef.current, {
                        childList: true,
                        subtree: true,
                    });
                }

                // Cleanup the observer on unmount
                return () => mutationObserver.current?.disconnect();
            }
        }
    });

    return { elementHeight: dynamicHeight };
};
